/**
 * ProjectHeader — Wave 5.9 build 4 (2026-07). Redesigned layout.
 *
 *   +----------+----------------------------+
 *   |          | Project name               |
 *   |  IMAGE   | 📍 site address            |
 *   |  ~1/4    | [Cat 1] [Cat 2] [Cat 3]    |
 *   |  width   |                            |
 *   +----------+----------------------------+
 *   Number · Description                     (secondary row)
 *   Primary Contact block                    (below, when present)
 *
 * Image uses object-fit: contain so the ENTIRE image is visible
 * (landscape or portrait), no cropping. Tasteful placeholder tile shown
 * when project.image is unset.
 *
 * Data source: `res.data.projects[]` from `POST /contractor-portal/login`.
 */
import { Briefcase, MapPin, User, Phone, Mail, Hash, Tag } from 'lucide-react';
import { thumbUrl, onThumbError } from '../utils/thumbUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProjectHeader({ project, index = 0 }) {
  if (!project) return null;

  const img = project.image || project.thumbnail;
  const specs = project.spec_categories || [];
  const contact = project.primary_contact;

  return (
    <div
      className="mb-4 rounded-xl overflow-hidden border border-[#2A2A2A] bg-[#141414]"
      data-testid={`project-header-${index}`}
    >
      {/* Top row: image (~1/4) left, name/address/pills right */}
      <div className="flex gap-3 p-3">
        {/* Image tile — 25% width, fixed aspect, object-contain to preserve full image */}
        <div
          className="flex-shrink-0 w-1/4 max-w-[160px] aspect-square bg-[#1A1A1A] rounded-lg overflow-hidden flex items-center justify-center border border-[#2A2A2A]"
          data-testid={`project-header-image-wrap-${index}`}
        >
          {img ? (
            <img
              src={thumbUrl(img, 320)}
              alt={project.name || 'Project'}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-contain"
              data-testid={`project-header-image-${index}`}
              onError={onThumbError(img)}
            />
          ) : (
            <Briefcase
              className="w-8 h-8 text-[#3A3A3A]"
              strokeWidth={1.5}
              data-testid={`project-header-placeholder-${index}`}
            />
          )}
        </div>

        {/* Right column: name, address, category pills */}
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          <p
            className="text-base sm:text-lg font-semibold text-[#E5E2DD] leading-tight truncate"
            data-testid={`project-header-name-${index}`}
          >
            {project.name || 'Project'}
          </p>

          {project.site_address ? (
            <p
              className="text-xs text-[#A9A6A0] flex items-start gap-1"
              data-testid={`project-header-address-${index}`}
            >
              <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-[#BC5D41]" />
              <span className="leading-snug">{project.site_address}</span>
            </p>
          ) : null}

          {specs.length > 0 && (
            <div
              className="flex flex-wrap gap-1 mt-0.5"
              data-testid={`project-header-specs-${index}`}
            >
              {specs.map((sc) => (
                <span
                  key={sc.prefix}
                  className="text-[10px] px-2 py-0.5 rounded-full bg-[#70170F]/20 text-[#BC5D41] border border-[#70170F]/40"
                  data-testid={`project-header-spec-${sc.prefix}`}
                >
                  {sc.name || sc.prefix}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Secondary row: number + description */}
      {(project.project_number || project.description) && (
        <div className="px-3 pb-3 border-t border-[#2A2A2A] pt-2.5 space-y-1.5">
          {project.project_number && (
            <p
              className="text-xs text-[#8A8A8A] flex items-center gap-1"
              data-testid={`project-header-number-${index}`}
            >
              <Hash className="w-3 h-3" /> {project.project_number}
            </p>
          )}
          {project.description && (
            <p
              className="text-xs text-[#8A8A8A] leading-relaxed"
              data-testid={`project-header-description-${index}`}
            >
              {project.description}
            </p>
          )}
        </div>
      )}

      {/* Primary contact block */}
      {contact && (contact.name || contact.email || contact.phone) && (
        <div
          className="px-3 pb-3 border-t border-[#2A2A2A] pt-2.5 space-y-1"
          data-testid={`project-header-primary-contact-${index}`}
        >
          <p className="text-[10px] uppercase tracking-wider text-[#5A5A5A]">
            Primary Contact
          </p>
          {contact.name && (
            <p className="text-xs text-[#E5E2DD] flex items-center gap-1.5">
              <User className="w-3 h-3 text-[#8A8A8A]" />
              <span>{contact.name}</span>
              {contact.role && (
                <span className="text-[10px] text-[#5A5A5A]">· {contact.role}</span>
              )}
            </p>
          )}
          {contact.phone && (
            <a
              href={`tel:${contact.phone}`}
              className="text-xs text-[#BC5D41] flex items-center gap-1.5 hover:underline"
              data-testid={`project-header-primary-contact-phone-${index}`}
            >
              <Phone className="w-3 h-3" /> {contact.phone}
            </a>
          )}
          {contact.email && (
            <a
              href={`mailto:${contact.email}`}
              className="text-xs text-[#BC5D41] flex items-center gap-1.5 hover:underline break-all"
              data-testid={`project-header-primary-contact-email-${index}`}
            >
              <Mail className="w-3 h-3" /> {contact.email}
            </a>
          )}
        </div>
      )}
    </div>
  );
}
